/**
 * Operations API client for the Auteur Cloud operational cockpit.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C05C. This is the browser half
 * of the campaigns / runs / receipts / approvals / devices surface. It is a
 * thin `fetch` wrapper with NO client-side authority, mirroring the posture
 * `api/billing.ts` (C04C) and `api/auth.ts` (C_AUTH) established:
 *
 * NO CLIENT-SIDE OPERATIONS AUTHORITY
 *   Every function below relays one server call. None computes a campaign
 *   status, a run state, an approval decision, a capability check, or a
 *   dispatch permission. Pause/resume/stop, approval decisions, and remote
 *   dispatch are all server-enforced; this client only renders what comes
 *   back. The one thing this file *asserts* is a precondition label: the
 *   dispatch helper refuses to build a request body unless both a signed
 *   envelope reference AND an approved approval id are present, because the
 *   backend (`auteur_cloud/bridge/**`) rejects unsigned or unapproved remote
 *   execution regardless of what any UI sends. That refusal is UX, not
 *   enforcement — enforcement stays server-side.
 *
 * SESSION, NOT TOKEN
 *   Same posture as `api/billing.ts`: the `__Host-auteur_session` cookie is
 *   the only credential; every call sets `credentials: 'include'`. Nothing
 *   here touches `localStorage`, `document.cookie`, or an `Authorization`
 *   header.
 *
 * MOUNTED ROUTES
 *   The three /v1/operations/devices* paths used for paired Local Nodes
 *   are mounted by the connections campaign. Campaign, run, approval, ceiling,
 *   and dispatch endpoints remain contracts for their owning backend work.
 *   The receipts paths reuse C03C's mounted routes.
 *
 * BUILD STATUS — compiled and exercised as part of the Vite customer SPA.
 */

import { ApiClientError, requestJson } from './http';

const OPS_BASE = '/v1/operations';
const RECEIPTS_BASE = '/v1/receipts';

/** The one shape every endpoint here returns on failure. */
export interface OperationsApiErrorBody {
  code: string;
  message: string;
  status: number;
}

/** A structured failure. Mirrors `BillingApiError` in `api/billing.ts`. */
export class OperationsApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'OperationsApiError';
    this.status = status;
    this.code = code;
  }

  /** The endpoint is not mounted, or a required service is not configured. */
  get isUnavailable(): boolean {
    return this.status === 503 || this.status === 404;
  }
}

async function request<T>(base: string, path: string, init: RequestInit = {}): Promise<T> {
  try {
    return (await requestJson(`${base}${path}`, init)) as T;
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw new OperationsApiError(error.status, error.code, error.message);
    }
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Campaigns — GET/POST /v1/operations/campaigns/** (contract; see file note)
// ---------------------------------------------------------------------------

/**
 * Contract shape for the campaign backend (not yet mounted; see file note).
 * Rendered verbatim; the UI never infers a status it was not given.
 * `source_local: true` marks a campaign whose authoritative state lives on
 * the owner's machine — the cockpit must show that badge, never paper over
 * it (C05C required property "Source-local status visible").
 */
export interface CampaignSummary {
  campaign_id: string;
  title: string;
  status: string;
  source_local: boolean;
  updated_at: string;
  [key: string]: unknown;
}

export interface CampaignListResponse {
  campaigns: CampaignSummary[];
  [key: string]: unknown;
}

export interface CampaignDetail extends CampaignSummary {
  task_graph: TaskGraphNode[];
  [key: string]: unknown;
}

/** One node of the campaign's task graph, as the server reports it. */
export interface TaskGraphNode {
  task_key: string;
  status: string;
  depends_on: string[];
  [key: string]: unknown;
}

export function fetchCampaigns(): Promise<CampaignListResponse> {
  return request<CampaignListResponse>(OPS_BASE, '/campaigns');
}

export function fetchCampaign(campaignId: string): Promise<CampaignDetail> {
  return request<CampaignDetail>(OPS_BASE, `/campaigns/${encodeURIComponent(campaignId)}`);
}

/** Lifecycle verbs. Each is one POST; the server owns the state machine. */
export function pauseCampaign(campaignId: string): Promise<CampaignDetail> {
  return request<CampaignDetail>(OPS_BASE, `/campaigns/${encodeURIComponent(campaignId)}/pause`, {
    method: 'POST',
  });
}

export function resumeCampaign(campaignId: string): Promise<CampaignDetail> {
  return request<CampaignDetail>(OPS_BASE, `/campaigns/${encodeURIComponent(campaignId)}/resume`, {
    method: 'POST',
  });
}

export function stopCampaign(campaignId: string): Promise<CampaignDetail> {
  return request<CampaignDetail>(OPS_BASE, `/campaigns/${encodeURIComponent(campaignId)}/stop`, {
    method: 'POST',
  });
}

// ---------------------------------------------------------------------------
// Runs and live events — GET /v1/operations/runs/** (contract)
// ---------------------------------------------------------------------------

export interface RunSummary {
  run_id: string;
  campaign_id: string;
  status: string;
  /** Where the run executes or reported from; rendered as a badge. */
  origin: string;
  started_at: string;
  [key: string]: unknown;
}

export interface RunEvent {
  event_id: string;
  at: string;
  level: string;
  message: string;
  [key: string]: unknown;
}

export interface RunListResponse {
  runs: RunSummary[];
  [key: string]: unknown;
}

export interface RunEventResponse {
  events: RunEvent[];
  [key: string]: unknown;
}

export function fetchRuns(campaignId?: string): Promise<RunListResponse> {
  const query = campaignId ? `?campaign_id=${encodeURIComponent(campaignId)}` : '';
  return request<RunListResponse>(OPS_BASE, `/runs${query}`);
}

export function fetchRun(runId: string): Promise<RunSummary> {
  return request<RunSummary>(OPS_BASE, `/runs/${encodeURIComponent(runId)}`);
}

/** Live event tail. Polling from the page; the server remains authoritative. */
export function fetchRunEvents(runId: string): Promise<RunEventResponse> {
  return request<RunEventResponse>(OPS_BASE, `/runs/${encodeURIComponent(runId)}/events`);
}

export function stopRun(runId: string): Promise<RunSummary> {
  return request<RunSummary>(OPS_BASE, `/runs/${encodeURIComponent(runId)}/stop`, {
    method: 'POST',
  });
}

export function pauseRun(runId: string): Promise<RunSummary> {
  return request<RunSummary>(OPS_BASE, `/runs/${encodeURIComponent(runId)}/pause`, {
    method: 'POST',
  });
}

// ---------------------------------------------------------------------------
// Receipts and mismatches — /v1/receipts/** (C03C route shapes)
// ---------------------------------------------------------------------------

/**
 * A receipt envelope projection. Field names mirror, field for field, the
 * projection C03C's `auteur_cloud/api/routes/receipts.py::_envelope_projection`
 * returns at this base ref (the index signature only future-proofs; every
 * named field below is observed in that Python source).
 */
export interface ReceiptEnvelope {
  envelope_id: string;
  project_id: string;
  source_ref: string;
  body_sha256: string;
  source_included: boolean;
  synced_by: string;
  synced_at: string;
  created_at: string;
  [key: string]: unknown;
}

export interface ReceiptListResponse {
  envelopes: ReceiptEnvelope[];
  [key: string]: unknown;
}

/**
 * A server-reported mismatch between a receipt the cloud holds and what the
 * local source of truth says. Rendered side-by-side; the UI never resolves
 * a mismatch itself.
 */
export interface ReceiptMismatch {
  mismatch_id: string;
  field: string;
  expected: string;
  observed: string;
  [key: string]: unknown;
}

export interface ReceiptMismatchResponse {
  mismatches: ReceiptMismatch[];
  [key: string]: unknown;
}

export function fetchReceipts(): Promise<ReceiptListResponse> {
  return request<ReceiptListResponse>(RECEIPTS_BASE, '');
}

export function fetchReceipt(envelopeId: string): Promise<ReceiptEnvelope> {
  return request<ReceiptEnvelope>(RECEIPTS_BASE, `/${encodeURIComponent(envelopeId)}`);
}

export function fetchReceiptMismatches(envelopeId: string): Promise<ReceiptMismatchResponse> {
  return request<ReceiptMismatchResponse>(
    RECEIPTS_BASE,
    `/${encodeURIComponent(envelopeId)}/mismatches`,
  );
}

// ---------------------------------------------------------------------------
// Approvals — /v1/operations/approvals/** (contract)
// ---------------------------------------------------------------------------

export interface ApprovalRequest {
  approval_id: string;
  status: 'pending' | 'approved' | 'denied' | string;
  subject: string;
  requested_by: string;
  created_at: string;
  [key: string]: unknown;
}

export interface ApprovalListResponse {
  approvals: ApprovalRequest[];
  [key: string]: unknown;
}

export interface ApprovalDecisionBody {
  /** Required. Empty reason strings are rejected by the caller too. */
  reason: string;
}

export function fetchApprovals(): Promise<ApprovalListResponse> {
  return request<ApprovalListResponse>(OPS_BASE, '/approvals');
}

export function approveRequest(approvalId: string, body: ApprovalDecisionBody): Promise<ApprovalRequest> {
  if (!body.reason.trim()) {
    return Promise.reject(
      new OperationsApiError(400, 'reason_required', 'an approval decision requires a reason'),
    );
  }
  return request<ApprovalRequest>(
    OPS_BASE,
    `/approvals/${encodeURIComponent(approvalId)}/approve`,
    { method: 'POST', body: JSON.stringify({ reason: body.reason }) },
  );
}

export function denyRequest(approvalId: string, body: ApprovalDecisionBody): Promise<ApprovalRequest> {
  if (!body.reason.trim()) {
    return Promise.reject(
      new OperationsApiError(400, 'reason_required', 'a denial requires a reason'),
    );
  }
  return request<ApprovalRequest>(
    OPS_BASE,
    `/approvals/${encodeURIComponent(approvalId)}/deny`,
    { method: 'POST', body: JSON.stringify({ reason: body.reason }) },
  );
}

// ---------------------------------------------------------------------------
// Budget ceilings — /v1/operations/ceilings/** (contract)
// ---------------------------------------------------------------------------

export interface CeilingState {
  scope: string;
  spent_usd: string;
  ceiling_usd: string;
  [key: string]: unknown;
}

export interface CeilingListResponse {
  ceilings: CeilingState[];
  [key: string]: unknown;
}

export interface CeilingUpdateBody {
  ceiling_usd: string;
  reason: string;
}

export function fetchCeilings(): Promise<CeilingListResponse> {
  return request<CeilingListResponse>(OPS_BASE, '/ceilings');
}

export function updateCeiling(scope: string, body: CeilingUpdateBody): Promise<CeilingState> {
  if (!body.reason.trim()) {
    return Promise.reject(
      new OperationsApiError(400, 'reason_required', 'a ceiling change requires a reason'),
    );
  }
  return request<CeilingState>(OPS_BASE, `/ceilings/${encodeURIComponent(scope)}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Devices, capabilities, and signed dispatch — /v1/operations/devices/**
// ---------------------------------------------------------------------------

/**
 * Mirrors `auteur_cloud/bridge/device_registry.py::DeviceRecord.projection()`
 * field for field (the full projection also carries `public_key`,
 * `key_algorithm`, `paired_at`, `paired_by`, `revoked_at`, `revoked_reason`;
 * the index signature covers those without this client naming a key path).
 */
export interface DeviceSummary {
  device_id: string;
  label: string;
  status: 'active' | 'revoked' | string;
  paired_at?: string;
  last_seen_at: string | null;
  revoked_at?: string | null;
  online?: boolean;
  connection_state?: 'connected' | 'offline';
  [key: string]: unknown;
}

/**
 * Mirrors `auteur_cloud/bridge/device_registry.py::CapabilityAdvertisement.
 * projection()` field for field (`capability_id`, `group`, `version`,
 * `health`, `advertised_at`).
 */
export interface DeviceCapability {
  capability_id: string;
  group: string;
  version: string;
  health: string;
  advertised_at: string;
  [key: string]: unknown;
}

export interface DeviceListResponse {
  devices: DeviceSummary[];
  counts?: { total: number; online: number; offline: number; revoked: number };
  online_window_seconds?: number;
  evaluated_at?: string;
  [key: string]: unknown;
}

export interface DeviceCapabilityResponse {
  capabilities: DeviceCapability[];
  [key: string]: unknown;
}

export function fetchDevices(): Promise<DeviceListResponse> {
  return request<DeviceListResponse>(OPS_BASE, '/devices');
}

export function fetchBridgePublicKey(): Promise<{ cloud_public_key: string }> {
  return request<{ cloud_public_key: string }>(OPS_BASE, '/bridge-public-key');
}

export function fetchDeviceCapabilities(deviceId: string): Promise<DeviceCapabilityResponse> {
  return request<DeviceCapabilityResponse>(
    OPS_BASE,
    `/devices/${encodeURIComponent(deviceId)}/capabilities`,
  );
}

/** Revoke one paired Local Node after the customer confirms in the UI. */
export function revokeDevice(deviceId: string): Promise<{ device: DeviceSummary }> {
  return request<{ device: DeviceSummary }>(
    OPS_BASE,
    '/devices/' + encodeURIComponent(deviceId) + '/revoke',
    { method: 'POST', body: JSON.stringify({}) },
  );
}

export type ExecutionPreference = 'local_only' | 'prefer_local' | 'either' | 'cloud_allowed';
export type SubscriptionLane = 'claude' | 'chatgpt_codex' | 'antigravity_gemini';

export interface NodeOperatingModes {
  execution_preference: ExecutionPreference;
  execution_preference_source: 'stored' | 'derived_default';
  derived_default: ExecutionPreference;
  has_active_node: boolean;
  lanes: Record<SubscriptionLane, { enabled: boolean }>;
  updated_at: string | null;
  enforced_by_scheduler: boolean;
}

export async function fetchNodeOperatingModes(): Promise<NodeOperatingModes> {
  const payload = await requestJson('/v1/node-operating-modes');
  return (payload as { node_operating_modes: NodeOperatingModes }).node_operating_modes;
}

export async function updateNodeOperatingModes(input: {
  execution_preference?: ExecutionPreference | null;
  lanes?: Partial<Record<SubscriptionLane, boolean | { enabled: boolean }>>;
}): Promise<NodeOperatingModes> {
  const payload = await requestJson('/v1/node-operating-modes', {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  return (payload as { node_operating_modes: NodeOperatingModes }).node_operating_modes;
}

/**
 * A remote-dispatch request. REQUIRED PROPERTY, C05C: no remote execution
 * without a signed envelope AND an approved approval. This helper refuses to
 * build the request body unless both are present — but that is a UX guard
 * only; `auteur_cloud/bridge/**` verifies the signature and the approval
 * server-side regardless, and this client never signs anything itself
 * (signing keys never leave the device that holds them).
 */
export interface DispatchRequestInput {
  deviceId: string;
  approvalId: string;
  /** Reference to an already-signed dispatch envelope. */
  signedEnvelopeRef: string;
  intent: string;
}

export function requestRemoteDispatch(input: DispatchRequestInput): Promise<Record<string, unknown>> {
  if (!input.approvalId.trim()) {
    return Promise.reject(
      new OperationsApiError(
        400,
        'approval_required',
        'remote dispatch requires an approved approval; the backend enforces this too',
      ),
    );
  }
  if (!input.signedEnvelopeRef.trim()) {
    return Promise.reject(
      new OperationsApiError(
        400,
        'signed_envelope_required',
        'remote dispatch requires a signed envelope; unsigned dispatch is rejected by the bridge',
      ),
    );
  }
  return request<Record<string, unknown>>(
    OPS_BASE,
    `/devices/${encodeURIComponent(input.deviceId)}/dispatch`,
    {
      method: 'POST',
      body: JSON.stringify({
        approval_id: input.approvalId,
        signed_envelope_ref: input.signedEnvelopeRef,
        intent: input.intent,
      }),
    },
  );
}
